import { contextFilesApi } from '@/lib/api';
import type { ContextFile } from '@mss_creator/shared';
import { Alert, Button } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

/**
 * Uploading the documents that feed generation as extra context (2026-08-26
 * design doc, D5-D8, D23).
 *
 * Extraction happens server-side during the upload, and an unusable file is
 * refused outright rather than stored — so every row listed here is a document
 * that will actually reach the model. That is why rejections are surfaced
 * prominently and per-cause instead of as a generic failure: the user has to
 * be able to tell "PDFs are not accepted" from "that file is corrupt".
 */

const ACCEPTED = '.txt,.md,.docx,.xlsx';

/**
 * The backend answers with a machine-readable `code` (`ContextFileRejection`)
 * and keeps its own messages language-neutral, so the French copy lives here.
 */
const REJECTION_MESSAGES: Record<string, string> = {
  PDF_NOT_ACCEPTED:
    'Les PDF ne sont pas acceptés : leur texte est souvent mal extrait. Exportez le document en .docx, ou collez son texte dans un .txt.',
  PDF_NO_EXTRACTABLE_TEXT:
    'Aucun texte extractible dans ce fichier : il est vide, ou ne contient que des images.',
  FILE_ENCRYPTED: 'Ce fichier est protégé par un mot de passe et ne peut pas être lu.',
  FILE_UNREADABLE: 'Ce fichier est illisible ou corrompu.',
  FILE_BINARY_CONTENT: 'Ce fichier contient des données binaires, pas du texte.',
  FILE_TYPE_UNSUPPORTED: 'Format non pris en charge. Acceptés : .txt, .md, .docx, .xlsx.',
  FILE_TOO_LARGE: 'Fichier trop volumineux — 25 Mo maximum.',
  CONFLICT: 'Les documents ne peuvent plus être modifiés une fois la génération lancée.',
};

function messageFor(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  if (code && REJECTION_MESSAGES[code]) return REJECTION_MESSAGES[code];
  return "Le téléversement a échoué. Réessayez, ou vérifiez que le fichier s'ouvre correctement.";
}

/** Bytes as the user thinks of them, not as a raw count. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function formatChars(chars: number): string {
  return chars >= 1000 ? `${Math.round(chars / 1000)} k caractères` : `${chars} caractères`;
}

export interface ContextFilesFieldProps {
  matrixId: string;
  /** Documents are fixed once generation starts — the guard is server-side too. */
  disabled?: boolean;
}

export function ContextFilesField({ matrixId, disabled = false }: ContextFilesFieldProps) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [rejection, setRejection] = useState<string | null>(null);

  const queryKey = ['context-files', matrixId];

  const { data: files = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => contextFilesApi.list(matrixId),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => contextFilesApi.upload(matrixId, file),
    onSuccess: () => {
      setRejection(null);
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => setRejection(messageFor(error)),
  });

  const removeMutation = useMutation({
    mutationFn: (fileId: string) => contextFilesApi.remove(matrixId, fileId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
    onError: (error) => setRejection(messageFor(error)),
  });

  const busy = uploadMutation.isPending || removeMutation.isPending;

  function handleSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Reset the input straight away so re-picking the same filename after a
    // rejection still fires a change event.
    event.target.value = '';
    if (!file) return;
    uploadMutation.mutate(file);
  }

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="font-semibold text-small">Documents de contexte</span>
        <span className="text-caption text-gray-600">Facultatif</span>
      </div>
      <p className="mt-1 text-gray-600 text-small">
        Briefs, études, notes d’atelier… Leur contenu est résumé puis fourni au modèle en plus du
        sujet et du contexte ci-dessus. Formats acceptés : .txt, .md, .docx, .xlsx — 25 Mo par
        fichier.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        onChange={handleSelected}
        disabled={disabled || busy}
        data-testid="context-file-input"
      />

      <div className="mt-3">
        <Button
          type="button"
          variant="primary"
          icon="attach_file"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || removeMutation.isPending}
          loading={uploadMutation.isPending}
          loadingLabel="Téléversement…"
        >
          Ajouter un document
        </Button>
      </div>

      {rejection && (
        <div className="mt-2">
          <Alert tone="danger">{rejection}</Alert>
        </div>
      )}

      {isLoading ? (
        <p className="mt-3 text-gray-500 text-small">Chargement des documents…</p>
      ) : (
        files.length > 0 && (
          <ul className="mt-3 space-y-2">
            {files.map((file) => (
              <ContextFileRow
                key={file.id}
                file={file}
                disabled={disabled || busy}
                onRemove={() => removeMutation.mutate(file.id)}
              />
            ))}
          </ul>
        )
      )}
    </div>
  );
}

interface ContextFileRowProps {
  file: ContextFile;
  disabled: boolean;
  onRemove: () => void;
}

function ContextFileRow({ file, disabled, onRemove }: ContextFileRowProps) {
  return (
    <li className="flex items-start justify-between gap-3 rounded-md border border-gray-200 px-3 py-2">
      <div className="min-w-0">
        <p className="truncate font-semibold text-small">{file.filename}</p>
        <p className="text-caption text-gray-600">
          {formatSize(file.sizeBytes)} · {formatChars(file.textChars)} extraits
        </p>
        {/* Truncation is never silent (D7): the user has to know part of the
            document will not reach the model. */}
        {file.truncated && (
          <div className="mt-1">
            <Alert tone="warning">
              Document tronqué : seuls les 200 000 premiers caractères seront utilisés.
            </Alert>
          </div>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        icon="delete"
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Retirer ${file.filename}`}
      />
    </li>
  );
}
